// src/resources/bookings/components/BookingActionsCompact.tsx
import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Edit, Receipt, CheckCircle, Email } from "@mui/icons-material";
import { useRecordContext, useNotify } from "react-admin";
import { apiUrl } from "../../../httpClient";
import { BOOKING_STATUS_OPTIONS } from "../statuses";

export const BookingActionsCompact = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [voucherDialogOpen, setVoucherDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [loading, setLoading] = useState({
    status: false,
    voucher: false,
  });

  const handleUpdateStatus = async () => {
    if (!record?.id) return;
    setLoading((prev) => ({ ...prev, status: true }));

    try {
      const response = await fetch(apiUrl(`/bookings/${record.id}/status`), {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status: newStatus,
          notes: "Status updated by admin",
        }),
      });

      if (response.ok) {
        notify("Booking status updated successfully", { type: "success" });
        setStatusDialogOpen(false);
        setNewStatus("");
      } else {
        const error = await response.json();
        notify(error.message || "Failed to update status", { type: "error" });
      }
    } catch {
      notify("Error updating status", { type: "error" });
    } finally {
      setLoading((prev) => ({ ...prev, status: false }));
    }
  };

  const handleResendVoucher = async () => {
    if (!record?.id) return;
    setLoading((prev) => ({ ...prev, voucher: true }));

    try {
      const response = await fetch(
        apiUrl(`/bookings/${record.id}/resend-voucher`),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: record.user?.email || record.user?.username,
          }),
        },
      );

      if (response.ok) {
        notify("Voucher resent successfully", { type: "success" });
        setVoucherDialogOpen(false);
      } else {
        const error = await response.json();
        let errorMessage = "Failed to resend voucher";

        // Handle specific error cases based on API documentation
        if (response.status === 400) {
          errorMessage = "Voucher can only be resent for confirmed bookings";
        } else if (response.status === 403) {
          errorMessage = "Access denied - Admin or SuperAdmin role required";
        } else if (response.status === 404) {
          errorMessage =
            "Booking not found, voucher PDF not found in S3 storage, or customer not found";
        } else if (response.status === 500) {
          errorMessage =
            "Internal server error - Email service failure or S3 storage access error";
        } else if (error.message) {
          errorMessage = error.message;
        }

        notify(errorMessage, { type: "error" });
      }
    } catch {
      notify("Network error - Unable to resend voucher", { type: "error" });
    } finally {
      setLoading((prev) => ({ ...prev, voucher: false }));
    }
  };

  // Action availability logic
  const canResendVoucher = record?.status === "confirmed";

  // Canonical status list — shared with the API's BOOKING_STATUSES.
  const statusOptions = BOOKING_STATUS_OPTIONS;

  return (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
      {/* Update Status Button */}
      <Button
        variant="outlined"
        size="small"
        startIcon={<Edit />}
        onClick={() => setStatusDialogOpen(true)}
        sx={{
          minWidth: "auto",
          px: 1,
          py: 0.5,
          fontSize: "0.75rem",
          textTransform: "none",
        }}
      >
        Update Status
      </Button>

      {/* Resend Voucher Button - Only for confirmed bookings */}
      {canResendVoucher && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<Receipt />}
          onClick={() => setVoucherDialogOpen(true)}
          sx={{
            minWidth: "auto",
            px: 1,
            py: 0.5,
            fontSize: "0.75rem",
            textTransform: "none",
            borderColor: "#4caf50",
            color: "#4caf50",
            "&:hover": {
              borderColor: "#45a049",
              backgroundColor: "rgba(76, 175, 80, 0.04)",
            },
          }}
        >
          Resend Voucher
        </Button>
      )}

      {/* Status Update Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Edit color="primary" />
          Update Booking Status
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Current Status:
            </Typography>
            <Chip
              label={record?.status
                ?.replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase())}
              color="primary"
              variant="outlined"
            />
          </Box>

          <FormControl fullWidth margin="dense">
            <InputLabel>New Status</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              label="New Status"
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box>
                    <Typography variant="body1">{option.label}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {option.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setStatusDialogOpen(false)}
            disabled={loading.status}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateStatus}
            color="primary"
            disabled={loading.status || !newStatus}
            startIcon={
              loading.status ? <CircularProgress size={16} /> : <CheckCircle />
            }
          >
            {loading.status ? "Updating..." : "Update Status"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resend Voucher Dialog */}
      <Dialog
        open={voucherDialogOpen}
        onClose={() => setVoucherDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Receipt color="success" />
          Resend Booking Voucher
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This will send the booking voucher to the customer&apos;s email
            address. The voucher must exist in S3 storage.
          </Alert>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Booking Details:
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Booking ID: {record?.id}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Customer: {record?.user?.name || record?.user?.username}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Email: {record?.user?.email || record?.user?.username}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Property: {record?.property?.name}
            </Typography>
          </Box>

          <Alert severity="warning">
            <Typography variant="body2">
              <strong>Requirements:</strong>
              <br />• Booking status must be &quot;confirmed&quot;
              <br />• Voucher PDF must exist in S3 storage
              <br />• Customer email must be valid
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setVoucherDialogOpen(false)}
            disabled={loading.voucher}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResendVoucher}
            color="success"
            disabled={loading.voucher}
            startIcon={
              loading.voucher ? <CircularProgress size={16} /> : <Email />
            }
          >
            {loading.voucher ? "Sending..." : "Resend Voucher"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
